// apps/client/src/pages/leads/MarketingSourceReportPage.tsx
//
// Monthly Report: lead volumes by source and campaign, with month-on-month
// movement, source mix, a spend trend, and cost-per-lead.
//
// Replaces the tally step of a manual monthly process (export leads from the
// CRM, read each client's Enquiry Source by hand in MyCRM, tally, compile in
// Canva). Valutten-captured leads and MyCRM deals are rendered as separate
// populations because a captured lead can later become a CRM deal.
//
// The scope caveat is rendered at the top, not buried in a footnote, and it
// comes from the payload rather than being hardcoded here so it cannot drift
// from what the query returns. Without it, "27 leads" for a month reads as the
// group's total lead flow, which it is not. The same content is destined for a
// shared, unauthenticated page where nobody can ask a clarifying question.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Megaphone,
  Info,
  Inbox,
  Save,
  Share2,
  Copy,
  Check,
  X,
  Lock,
  Trash2,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import { PageHeader } from '../../components/common/PageHeader';
import { isMonthInProgress } from '../../utils/businessTime';
import {
  useMarketingSourceReport,
  type LeadMonth,
  type ProviderSpend,
  type CampaignSpend,
  type CrmEnquirySourceMonth,
} from '../../hooks/useMarketingSourceReport';

interface OutletCtx {
  userData?: { companyId?: string; role?: string };
  // The per-tenant share kill switch lives here. Same shape the Dashboard
  // reads for the sibling company_overview_share flag.
  companyData?: { featureFlags?: Record<string, unknown> };
}

// Roles the getMarketingSourceReport callable accepts.
const REPORT_ROLES = ['super_admin', 'administrator', 'marketing'];

interface Panels {
  social: string;
  seo: string;
  leadGen: string;
}

const EMPTY_PANELS: Panels = { social: '', seo: '', leadGen: '' };

const EMPTY_CRM_ENQUIRY_SOURCE: CrmEnquirySourceMonth = {
  totalDeals: 0,
  knownDeals: 0,
  unknownDeals: 0,
  coveragePct: 0,
  byCategory: [],
};

const PANEL_FIELDS: Array<{ key: keyof Panels; label: string; placeholder: string }> = [
  { key: 'social', label: 'Social media', placeholder: 'Reach, engagement, notable posts…' },
  { key: 'seo', label: 'SEO', placeholder: 'Rankings, traffic, content published…' },
  { key: 'leadGen', label: 'Lead generation', placeholder: 'Campaign notes, spend, what changed…' },
];

const MAX_PANEL_LENGTH = 5000;

/** website_form / meta_ads -> Website Form / Meta Ads. */
const prettySource = (s: string) =>
  s
    .split('_')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

/** '2026-06' -> 'June 2026'. Parsed as UTC so the label cannot slip a month. */
const prettyMonth = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-AU', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

/** '2026-06' -> 'Jun 26' for compact axis / tab labels. */
const shortMonth = (m: string) => {
  const [y, mo] = m.split('-').map(Number);
  if (!y || !mo) return m;
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString('en-AU', {
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });
};

/** '2026-06' -> '2026-05' (previous calendar month). */
const prevCalendarMonth = (m: string): string | null => {
  const [y, mo] = m.split('-').map(Number);
  if (!y || !mo) return null;
  const d = new Date(Date.UTC(y, mo - 2, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** Integer cents -> AUD. dp=0 for totals, dp=2 for cost-per-lead. */
const aud = (cents: number, dp = 0) =>
  `$${(cents / 100).toLocaleString('en-AU', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;

const StatCard: React.FC<{ label: string; value: string; hint?: React.ReactNode }> = ({
  label,
  value,
  hint,
}) => (
  <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
    <div className="text-[11px] uppercase tracking-wider text-gray-400">{label}</div>
    <div className="mt-1 text-2xl font-semibold">{value}</div>
    {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
  </div>
);

/**
 * Month-on-month delta for a lead count. Up is good (green), because more leads
 * is better. Deliberately NOT reused for cost-per-lead, where up is bad.
 *
 * NEVER rendered for a month still in progress. Four days of one month against
 * thirty of the last is a guaranteed red arrow that says nothing about
 * performance, and it was the first thing the operator saw every month.
 */
const LeadDelta: React.FC<{ curr: number; prev: number }> = ({ curr, prev }) => {
  const diff = curr - prev;
  if (curr === 0 && prev === 0) return <span className="text-gray-500">n/a</span>;
  const pctChange = prev === 0 ? null : Math.round((diff / prev) * 100);
  const Icon = diff === 0 ? Minus : diff > 0 ? TrendingUp : TrendingDown;
  const color = diff === 0 ? 'text-gray-400' : diff > 0 ? 'text-green-400' : 'text-red-400';
  return (
    <span className={`inline-flex items-center gap-1 ${color}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {diff > 0 ? '+' : ''}
      {diff}
      {pctChange !== null && (
        <span className="text-gray-500">
          ({pctChange > 0 ? '+' : ''}
          {pctChange}%)
        </span>
      )}
    </span>
  );
};

/** Leads-and-spend trend across every month, chronological left to right. */
const TrendChart: React.FC<{ months: LeadMonth[] }> = ({ months }) => {
  const data = useMemo(
    () =>
      months
        .slice()
        .sort((a, b) => a.month.localeCompare(b.month))
        .map((m) => {
          const partial = m.monthToDate ?? isMonthInProgress(m.month);
          return {
            // Labelled in the axis itself, so the short final bar is explained
            // where it is seen rather than in a footnote below the fold.
            month: partial ? `${shortMonth(m.month)} (MTD)` : shortMonth(m.month),
            leads: m.totalLeads,
            // Undefined, not 0, when no spend row exists: Recharts draws a gap
            // rather than a line dropping to the floor.
            spend: m.hasSpendRows === false ? undefined : Math.round(m.totalSpendCents / 100),
            partial,
          };
        }),
    [months],
  );

  const hasPartial = data.some((d) => d.partial);

  if (data.length < 2) return null;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
      <div className="text-[11px] uppercase tracking-wider text-gray-400 mb-3">
        Leads and spend over time
      </div>
      {hasPartial && (
        <p className="-mt-2 mb-3 text-xs text-amber-400/90">
          The month marked MTD is still running, so its bar covers part of a month
          and is not comparable with the completed months beside it.
        </p>
      )}
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
            <CartesianGrid stroke="#374151" strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fill: '#9ca3af', fontSize: 11 }} stroke="#374151" />
            <YAxis
              yAxisId="leads"
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              stroke="#374151"
              allowDecimals={false}
            />
            <YAxis
              yAxisId="spend"
              orientation="right"
              tick={{ fill: '#9ca3af', fontSize: 11 }}
              stroke="#374151"
              tickFormatter={(v: number) => `$${v.toLocaleString('en-AU')}`}
            />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid #374151', borderRadius: 8 }}
              labelStyle={{ color: '#e5e7eb' }}
              formatter={(value: number, name: string) =>
                name === 'Spend' ? `$${value.toLocaleString('en-AU')}` : value
              }
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="leads" dataKey="leads" name="Leads" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            <Line
              yAxisId="spend"
              type="monotone"
              dataKey="spend"
              name="Spend"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/** Source mix for one month: share of leads by channel, as labelled bars. */
const SourceMix: React.FC<{ month: LeadMonth; prev: LeadMonth | null }> = ({ month, prev }) => {
  const prevBySource = useMemo(() => {
    const m = new Map<string, number>();
    prev?.bySource.forEach((s) => m.set(s.source, s.leads));
    return m;
  }, [prev]);

  if (month.totalLeads === 0) return null;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700">
        <h3 className="text-base font-semibold">By source</h3>
        {prev && (
          <p className="text-xs text-gray-400 mt-0.5">Change compared with {prettyMonth(prev.month)}</p>
        )}
      </div>
      <div className="p-5 space-y-3">
        {month.bySource.map((s) => {
          const share = Math.round((s.leads / month.totalLeads) * 100);
          return (
            <div key={s.source}>
              <div className="flex items-center justify-between gap-4 text-sm mb-1">
                <span>{prettySource(s.source)}</span>
                <span className="flex items-center gap-3">
                  {prev && <LeadDelta curr={s.leads} prev={prevBySource.get(s.source) ?? 0} />}
                  <span className="font-mono text-gray-300">
                    {s.leads} <span className="text-gray-500">({share}%)</span>
                  </span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-700 overflow-hidden">
                <div className="h-full rounded-full bg-blue-500" style={{ width: `${share}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * MyCRM enquiry-source mix for one CRM-created month. This is deliberately not
 * folded into SourceMix: the latter counts Valutten-captured leads, while this
 * counts distinct MyCRM deals and the populations can overlap.
 */
const EnquirySourcePanel: React.FC<{
  data: CrmEnquirySourceMonth;
  note: string;
}> = ({ data, note }) => {
  if (data.totalDeals === 0) return null;

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700 flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-base font-semibold">MyCRM enquiry source</h3>
          <p className="text-xs text-gray-400 mt-0.5">Distinct CRM deals created this month</p>
        </div>
        <span className="text-sm text-gray-300">
          {data.coveragePct}% known
        </span>
      </div>
      <div className="p-5 space-y-3">
        {data.byCategory.map((row) => {
          const share = Math.round((row.deals / data.totalDeals) * 100);
          return (
            <div key={`${row.isUnknown ? 'unknown' : 'known'}:${row.category}`}>
              <div className="flex items-center justify-between gap-4 text-sm mb-1">
                <span className={row.isUnknown ? 'text-amber-300' : undefined}>{row.category}</span>
                <span className="font-mono text-gray-300">
                  {row.deals} <span className="text-gray-500">({share}%)</span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-gray-700 overflow-hidden">
                <div
                  className={`h-full rounded-full ${row.isUnknown ? 'bg-amber-500' : 'bg-indigo-500'}`}
                  style={{ width: `${share}%` }}
                />
              </div>
            </div>
          );
        })}
        <p className="pt-1 text-xs text-gray-500">{note}</p>
      </div>
    </div>
  );
};

/** Paid ad spend and cost-per-lead by provider for one month. */
const SpendPanel: React.FC<{ spendByProvider: ProviderSpend[]; spendNote: string }> = ({
  spendByProvider,
  spendNote,
}) => {
  if (spendByProvider.length === 0) return null;
  const hasZeroLeadSpend = spendByProvider.some((p) => p.spendCents > 0 && p.capturedLeads === 0);

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700">
        <h3 className="text-base font-semibold">Ad spend &amp; cost per lead</h3>
      </div>
      <div className="p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-400 text-left">
              <th className="pb-2 font-medium">Provider</th>
              <th className="pb-2 font-medium text-right">Spend</th>
              <th className="pb-2 font-medium text-right">Captured leads</th>
              <th className="pb-2 font-medium text-right">Cost / lead</th>
              <th className="pb-2 font-medium text-right">Clicks</th>
            </tr>
          </thead>
          <tbody>
            {spendByProvider.map((p) => (
              <tr key={p.provider} className="border-t border-gray-700/60">
                <td className="py-2">{prettySource(p.provider)}</td>
                <td className="py-2 text-right font-mono">{aud(p.spendCents)}</td>
                <td className="py-2 text-right font-mono">{p.capturedLeads}</td>
                <td className="py-2 text-right font-mono">
                  {p.costPerLeadCents !== null ? (
                    aud(p.costPerLeadCents, 2)
                  ) : (
                    <span className="text-amber-400">no leads</span>
                  )}
                </td>
                <td className="py-2 text-right font-mono text-gray-400">{p.clicks}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {hasZeroLeadSpend && (
          <p className="mt-3 text-xs text-amber-400/90">
            Some spend captured no leads this month. That spend still counts; cost per lead is left
            blank rather than dividing by zero.
          </p>
        )}
        <p className="mt-2 text-xs text-gray-500">{spendNote}</p>
      </div>
    </div>
  );
};

/** Campaign performance for one month: leads, spend and cost-per-lead. */
const CampaignPanel: React.FC<{
  byCampaign: LeadMonth['byCampaign'];
  spendByCampaign: CampaignSpend[];
}> = ({ byCampaign, spendByCampaign }) => {
  // Union of campaigns seen in leads and in spend, matched case-insensitively.
  const rows = useMemo(() => {
    const key = (s: string) => s.trim().toLowerCase();
    const map = new Map<
      string,
      { campaign: string; source: string; leads: number; spendCents: number | null; cplCents: number | null }
    >();
    for (const c of byCampaign) {
      map.set(key(c.campaign), {
        campaign: c.campaign,
        source: c.source,
        leads: c.leads,
        spendCents: null,
        cplCents: null,
      });
    }
    for (const s of spendByCampaign) {
      const k = key(s.campaign);
      const existing = map.get(k);
      if (existing) {
        existing.spendCents = s.spendCents;
        existing.cplCents = s.costPerLeadCents;
      } else {
        map.set(k, {
          campaign: s.campaign,
          source: s.provider,
          leads: s.capturedLeads,
          spendCents: s.spendCents,
          cplCents: s.costPerLeadCents,
        });
      }
    }
    return [...map.values()].sort(
      (a, b) => b.leads - a.leads || (b.spendCents ?? 0) - (a.spendCents ?? 0),
    );
  }, [byCampaign, spendByCampaign]);

  if (rows.length === 0) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-5">
        <h3 className="text-base font-semibold mb-2">By campaign</h3>
        <p className="text-sm text-gray-400">
          No campaign tags this month. Organic and direct enquiries carry none.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700">
        <h3 className="text-base font-semibold">By campaign</h3>
      </div>
      <div className="p-5 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-gray-400 text-left">
              <th className="pb-2 font-medium">Campaign</th>
              <th className="pb-2 font-medium text-right">Leads</th>
              <th className="pb-2 font-medium text-right">Spend</th>
              <th className="pb-2 font-medium text-right">Cost / lead</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.source}::${r.campaign}`} className="border-t border-gray-700/60">
                <td className="py-2">
                  <span className="block truncate max-w-xs">{r.campaign}</span>
                  <span className="text-xs text-gray-400">{prettySource(r.source)}</span>
                </td>
                <td className="py-2 text-right font-mono">{r.leads}</td>
                <td className="py-2 text-right font-mono">
                  {r.spendCents !== null ? aud(r.spendCents) : <span className="text-gray-600">—</span>}
                </td>
                <td className="py-2 text-right font-mono">
                  {r.cplCents !== null ? (
                    aud(r.cplCents, 2)
                  ) : r.spendCents !== null ? (
                    <span className="text-amber-400">no leads</span>
                  ) : (
                    <span className="text-gray-600">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Free-text commentary stored per Brisbane calendar month, so one shared link
 * carries the whole monthly report rather than the generated figures living
 * apart from the narrative around them.
 *
 * Rendered and edited as PLAIN TEXT throughout. This content reaches the public
 * shared report page, so it must never be injected as HTML.
 */
const CommentaryPanels: React.FC<{
  companyId: string;
  month: string;
  /**
   * Raised whenever the editor holds unsaved text. The share generator
   * snapshots SAVED commentary only, so minting a link mid-edit silently ships
   * a report without the words the author had just written.
   */
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ companyId, month, onDirtyChange }) => {
  const [panels, setPanels] = useState<Panels>(EMPTY_PANELS);
  const [savedPanels, setSavedPanels] = useState<Panels>(EMPTY_PANELS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const dirty =
    panels.social !== savedPanels.social
    || panels.seo !== savedPanels.seo
    || panels.leadGen !== savedPanels.leadGen;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    if (!companyId || !month) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setSavedAt(null);
      try {
        const fn = httpsCallable<unknown, { panels: Panels }>(functions, 'leadCaptureAction');
        const r = await fn({ action: 'getMarketingReportPanels', companyId, periodMonth: month });
        if (cancelled) return;
        const loaded: Panels = {
          social: r.data.panels?.social ?? '',
          seo: r.data.panels?.seo ?? '',
          leadGen: r.data.panels?.leadGen ?? '',
        };
        setPanels(loaded);
        setSavedPanels(loaded);
      } catch (err) {
        if (cancelled) return;
        // Not silently swallowed: an empty editor after a failed read looks
        // identical to "nothing written yet", and saving over it would then
        // destroy the month's commentary.
        console.error('[MarketingSourceReport] panel load failed:', err);
        setError(err instanceof Error ? err.message : 'Could not load this month’s commentary');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [companyId, month]);

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const fn = httpsCallable<unknown, { success: boolean }>(functions, 'leadCaptureAction');
      await fn({ action: 'saveMarketingReportPanels', companyId, periodMonth: month, panels });
      setSavedPanels(panels);
      setSavedAt(Date.now());
    } catch (err) {
      console.error('[MarketingSourceReport] panel save failed:', err);
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setSaving(false);
    }
  }, [companyId, month, panels]);

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-700">
        <h3 className="text-base font-semibold">Commentary</h3>
        <p className="text-xs text-gray-400 mt-0.5">{prettyMonth(month)}</p>
      </div>

      <div className="p-5 space-y-4">
        {error && (
          <div className="rounded-md border border-red-900/60 bg-red-950/30 p-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {PANEL_FIELDS.map((f) => (
          <div key={f.key}>
            <label
              htmlFor={`panel-${f.key}`}
              className="block text-[11px] uppercase tracking-wider text-gray-400 mb-1"
            >
              {f.label}
            </label>
            <textarea
              id={`panel-${f.key}`}
              rows={4}
              maxLength={MAX_PANEL_LENGTH}
              disabled={loading || saving}
              value={panels[f.key]}
              placeholder={f.placeholder}
              onChange={(e) => setPanels((p) => ({ ...p, [f.key]: e.target.value }))}
              className="w-full rounded-md border border-gray-600 bg-gray-900 p-3 text-sm disabled:opacity-60"
            />
          </div>
        ))}

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={save}
            disabled={loading || saving}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium hover:bg-blue-500 disabled:opacity-60"
          >
            <Save className="h-4 w-4" aria-hidden="true" />
            {saving ? 'Saving…' : 'Save commentary'}
          </button>
          {dirty && !saving && (
            <span className="text-sm text-amber-400">Unsaved changes</span>
          )}
          {savedAt && !dirty && !saving && <span className="text-sm text-gray-400">Saved</span>}
        </div>
      </div>
    </div>
  );
};

const EXPIRY_CHOICES = [7, 30, 90];

interface ShareSummary {
  shareId: string;
  createdAt: string;
  createdByEmail: string | null;
  expiresAt: string | null;
  expired: boolean;
  spendIncluded: boolean;
  panelMonth: string | null;
  viewCount: number;
  shareUrl: string;
}

const shortDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * Mints an expiring, revocable link to a read-only snapshot of this report for
 * recipients without a login, and lists what is already outstanding.
 *
 * THREE THINGS THIS MODAL OWES THE OPERATOR
 * -----------------------------------------
 * 1. WHAT THE LINK SHOWS, in plain words. The snapshot can carry ad spend and
 *    cost per lead to anyone holding the URL. The modal previously described it
 *    only as "a snapshot taken now".
 * 2. THE SAFE DEFAULT. Spend is left OUT unless it is asked for, so the choice
 *    made by not reading carefully is the conservative one.
 * 3. A WAY TO UNDO IT. The old copy called the link revocable and offered no
 *    revoke and no list of what was live.
 */
const ShareModal: React.FC<{
  companyId: string;
  panelMonth: string | null;
  commentaryDirty: boolean;
  onClose: () => void;
}> = ({ companyId, panelMonth, commentaryDirty, onClose }) => {
  const [days, setDays] = useState(30);
  const [includeSpend, setIncludeSpend] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [shares, setShares] = useState<ShareSummary[] | null>(null);
  const [sharesError, setSharesError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const loadShares = useCallback(async () => {
    setSharesError(null);
    try {
      const fn = httpsCallable<{ companyId: string }, { shares: ShareSummary[] }>(
        functions,
        'listMarketingReportShares',
      );
      const r = await fn({ companyId });
      setShares(r.data.shares ?? []);
    } catch (err) {
      // Surfaced, not swallowed: an empty list after a failed read looks
      // identical to "nothing is shared", which is the opposite of the truth
      // the operator came here for.
      console.error('[MarketingSourceReport] share list failed:', err);
      setShares([]);
      setSharesError('Could not load the active links. They may still be live.');
    }
  }, [companyId]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const generate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const fn = httpsCallable<
        { companyId: string; panelMonth?: string; expiresInDays: number; includeSpend: boolean },
        { shareUrl: string; expiresAt?: string }
      >(functions, 'generateMarketingReportShareToken');
      const r = await fn({
        companyId,
        ...(panelMonth ? { panelMonth } : {}),
        expiresInDays: days,
        includeSpend,
      });
      setUrl(r.data.shareUrl);
      setExpiresAt(r.data.expiresAt ?? null);
      loadShares();
    } catch (err) {
      console.error('[MarketingSourceReport] share generation failed:', err);
      setError(err instanceof Error ? err.message : 'Could not create a share link');
    } finally {
      setBusy(false);
    }
  }, [companyId, panelMonth, days, includeSpend, loadShares]);

  const revoke = useCallback(
    async (shareId: string) => {
      setRevoking(shareId);
      setSharesError(null);
      try {
        const fn = httpsCallable<{ companyId: string; shareId: string }, { revoked: boolean }>(
          functions,
          'revokeMarketingReportShare',
        );
        await fn({ companyId, shareId });
        await loadShares();
      } catch (err) {
        console.error('[MarketingSourceReport] share revoke failed:', err);
        setSharesError(
          err instanceof Error ? err.message : 'Could not revoke that link. It is still live.',
        );
      } finally {
        setRevoking(null);
      }
    },
    [companyId, loadShares],
  );

  const copy = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked by permissions; the input is selectable, so
      // failing to copy is not failing to share.
      setError('Could not copy automatically. Select the link and copy it.');
    }
  }, [url]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg border border-gray-700 bg-gray-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-700 px-5 py-3">
          <h3 className="text-base font-semibold">Share this report</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <p className="text-sm text-gray-400">
            Creates a read-only link that anyone can open without a VALUTTEN login. It shows a
            snapshot taken now, including this month&rsquo;s saved commentary, and stops working
            when it expires.
          </p>

          {/* The disclosure, stated plainly and before the button. */}
          <div className="rounded-md border border-amber-900/60 bg-amber-950/20 p-3 text-sm text-amber-200/90">
            Anyone who has the link can see lead counts and campaign names
            {includeSpend ? ', ad spend and cost per lead' : ''}. There is no login and no way to
            tell who opened it.
          </div>

          {error && (
            <div className="rounded-md border border-red-900/60 bg-red-950/30 p-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {!url ? (
            <>
              <div>
                <span className="block text-[11px] uppercase tracking-wider text-gray-400 mb-1">
                  Expires after
                </span>
                <div className="flex gap-2">
                  {EXPIRY_CHOICES.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setDays(d)}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        days === d
                          ? 'border-blue-500 bg-blue-600/20 text-blue-200'
                          : 'border-gray-600 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      {d} days
                    </button>
                  ))}
                </div>
              </div>

              {/* Default OFF: spend is left out unless it is asked for. */}
              <label className="flex items-start gap-3 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={includeSpend}
                  onChange={(e) => setIncludeSpend(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-gray-800"
                />
                <span>
                  Include ad spend and cost per lead
                  <span className="block text-xs text-gray-500">
                    Off by default. Leave it off to share lead volumes and campaign names without
                    publishing what they cost.
                  </span>
                </span>
              </label>

              {/* A7: the generator snapshots SAVED commentary only. */}
              {commentaryDirty && (
                <div className="flex gap-2 rounded-md border border-amber-900/60 bg-amber-950/20 p-3 text-sm text-amber-200/90">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
                  <span>
                    You have unsaved commentary. The link snapshots saved commentary only, so save
                    it first or the report you send will be missing what you just wrote.
                  </span>
                </div>
              )}

              <button
                type="button"
                onClick={generate}
                disabled={busy || commentaryDirty}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium hover:bg-blue-500 disabled:opacity-60"
              >
                <Share2 className="h-4 w-4" aria-hidden="true" />
                {busy ? 'Creating…' : 'Create link'}
              </button>
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={url}
                  onFocus={(e) => e.currentTarget.select()}
                  className="w-full rounded-md border border-gray-600 bg-gray-800 px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex shrink-0 items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium hover:bg-blue-500"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              {expiresAt && (
                <p className="text-sm text-gray-400">Expires {shortDateTime(expiresAt)}.</p>
              )}
            </>
          )}

          {/* Active links: what is outstanding, and the way to undo it. */}
          <div className="border-t border-gray-700 pt-4">
            <h4 className="text-sm font-semibold mb-2">Active links</h4>
            {sharesError && (
              <div className="mb-2 rounded-md border border-red-900/60 bg-red-950/30 p-2 text-xs text-red-300">
                {sharesError}
              </div>
            )}
            {shares === null ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : shares.length === 0 ? (
              <p className="text-sm text-gray-400">No links are currently live.</p>
            ) : (
              <ul className="space-y-2">
                {shares.map((sh) => (
                  <li
                    key={sh.shareId}
                    className="flex items-start justify-between gap-3 rounded-md border border-gray-700 bg-gray-800/40 p-3"
                  >
                    <div className="min-w-0 text-xs text-gray-400">
                      <div className="text-gray-200">
                        {sh.panelMonth ? prettyMonth(sh.panelMonth) : 'Whole series'}
                        {sh.spendIncluded && (
                          <span className="ml-2 rounded bg-amber-900/40 px-1.5 py-0.5 text-[10px] text-amber-200">
                            includes spend
                          </span>
                        )}
                        {sh.expired && (
                          <span className="ml-2 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] text-gray-300">
                            expired
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5">
                        Created {shortDateTime(sh.createdAt)}
                        {sh.createdByEmail ? ` by ${sh.createdByEmail}` : ''}
                      </div>
                      <div>
                        {sh.expiresAt ? `Expires ${shortDateTime(sh.expiresAt)}` : 'No expiry'}
                        {' · '}
                        {sh.viewCount} {sh.viewCount === 1 ? 'view' : 'views'}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => revoke(sh.shareId)}
                      disabled={revoking === sh.shareId}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-red-900/60 px-2.5 py-1.5 text-xs text-red-300 hover:border-red-700 disabled:opacity-60"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      {revoking === sh.shareId ? 'Revoking…' : 'Revoke'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <p className="mt-2 text-xs text-gray-500">
              Revoking stops the link working immediately, for everyone who has it.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const MarketingSourceReportPage: React.FC = () => {
  const { userData, companyData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  const role = userData?.role;

  // A1: the Share button rendered whenever a report existed, but the generator
  // refuses unless this per-tenant flag is on, and it is off for every tenant.
  // Every press was a 403. Same gate the Dashboard uses for the sibling share.
  const canShare = companyData?.featureFlags?.marketing_report_share === true;

  const [shareOpen, setShareOpen] = useState(false);
  const [commentaryDirty, setCommentaryDirty] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

  const { report, loading, error, refetch } = useMarketingSourceReport({
    companyId: companyId || '',
  });

  const months = report?.months ?? [];

  // Default to the most recent month once data arrives; keep the user's choice
  // across refetches if it still exists.
  useEffect(() => {
    if (months.length === 0) {
      setSelectedMonth(null);
      return;
    }
    setSelectedMonth((cur) => (cur && months.some((m) => m.month === cur) ? cur : months[0].month));
  }, [months]);

  const byMonth = useMemo(() => {
    const m = new Map<string, LeadMonth>();
    months.forEach((x) => m.set(x.month, x));
    return m;
  }, [months]);

  const current = selectedMonth ? byMonth.get(selectedMonth) ?? null : null;
  const currentIsPartial = current
    ? (current.monthToDate ?? isMonthInProgress(current.month))
    : false;
  const currentCrmEnquirySource = current?.crmEnquirySource ?? EMPTY_CRM_ENQUIRY_SOURCE;
  const prevMonthKey = selectedMonth ? prevCalendarMonth(selectedMonth) : null;
  const previous = prevMonthKey ? byMonth.get(prevMonthKey) ?? null : null;

  if (role && !REPORT_ROLES.includes(role)) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <Lock className="mx-auto mb-3 h-8 w-8 text-gray-500" aria-hidden="true" />
          <h2 className="text-sm font-semibold mb-2">
            You do not have access to the Monthly Report
          </h2>
          <p className="text-xs text-gray-400">
            This report is available to super admins, administrators and the marketing role.
            Ask an administrator if you need it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Megaphone}
        title="Monthly Report"
        subtitle="Lead acquisition and MyCRM enquiry sources"
        onRefresh={refetch}
        isRefreshing={loading}
        actions={
          report && months.length > 0 && canShare ? (
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-600 px-3 py-2 text-sm font-medium hover:border-gray-500"
            >
              <Share2 className="h-4 w-4" aria-hidden="true" />
              Share
            </button>
          ) : undefined
        }
      />

      {shareOpen && companyId && (
        <ShareModal
          companyId={companyId}
          panelMonth={selectedMonth}
          commentaryDirty={commentaryDirty}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* Scope note first, before any number. It is the difference between a
          reader understanding this month's figure and misreading it. */}
      {report && (
        <div className="rounded-lg border border-blue-900/60 bg-blue-950/30 p-4 flex gap-3">
          <Info className="h-5 w-5 shrink-0 text-blue-400 mt-0.5" aria-hidden="true" />
          <div className="text-sm text-gray-300">
            <p>{report.provenance.scopeNote}</p>
            {report.provenance.enquirySourceAvailable ? (
              <p className="mt-2 text-gray-400">{report.provenance.enquirySourceNote}</p>
            ) : (
              <p className="mt-2 text-gray-400">
                No MyCRM deals are available in the recent enquiry-source reporting window yet.
              </p>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-900/60 bg-red-950/30 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {loading && !report && <p className="text-sm text-gray-400">Loading report…</p>}

      {report && months.length === 0 && !loading && (
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 p-8 text-center">
          <Inbox className="mx-auto h-8 w-8 text-gray-500" aria-hidden="true" />
          <p className="mt-3 text-sm text-gray-400">No captured leads in this period yet.</p>
        </div>
      )}

      {report && months.length > 0 && (
        <>
          {/* Headline totals across the whole series. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
            <StatCard label="Leads captured" value={String(report.totals.totalLeads)} />
            <StatCard
              label="Total ad spend"
              value={aud(report.totals.totalSpendCents)}
              hint="Across the series"
            />
            <StatCard
              label="Cost / lead"
              value={
                report.totals.overallCostPerLeadCents !== null
                  ? aud(report.totals.overallCostPerLeadCents, 2)
                  : 'n/a'
              }
              hint="Paid providers"
            />
            <StatCard label="Campaigns" value={String(report.totals.distinctCampaigns)} />
            <StatCard
              label="Series starts"
              value={
                report.totals.firstCapturedMonth
                  ? prettyMonth(report.totals.firstCapturedMonth)
                  : 'n/a'
              }
              hint="Earliest captured lead"
            />
          </div>

          <TrendChart months={months} />

          {/* Month navigator: pick one month to inspect in detail. */}
          <div className="flex flex-wrap gap-2">
            {months.map((m) => (
              <button
                key={m.month}
                type="button"
                onClick={() => setSelectedMonth(m.month)}
                className={`rounded-md border px-3 py-1.5 text-sm ${
                  selectedMonth === m.month
                    ? 'border-blue-500 bg-blue-600/20 text-blue-200'
                    : 'border-gray-600 text-gray-300 hover:border-gray-500'
                }`}
              >
                {prettyMonth(m.month)}
                {(m.monthToDate ?? isMonthInProgress(m.month)) && (
                  <span className="ml-1.5 text-[10px] uppercase tracking-wider text-amber-400">
                    to date
                  </span>
                )}
              </button>
            ))}
          </div>

          {current && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5 gap-4">
                <StatCard
                  label={currentIsPartial ? 'Leads this month to date' : 'Leads this month'}
                  value={String(current.totalLeads)}
                  hint={
                    currentIsPartial ? (
                      // No month-on-month arrow on a part month: days against a
                      // full month is a guaranteed red that means nothing.
                      <span className="text-amber-400">
                        Month to date, not comparable with a full month
                      </span>
                    ) : previous && !(previous.monthToDate ?? isMonthInProgress(previous.month)) ? (
                      <LeadDelta curr={current.totalLeads} prev={previous.totalLeads} />
                    ) : undefined
                  }
                />
                <StatCard
                  label="Spend this month"
                  value={current.hasSpendRows === false ? 'Not available' : aud(current.totalSpendCents)}
                  hint={
                    current.hasSpendRows === false
                      ? 'No ad spend data has arrived for this month'
                      : undefined
                  }
                />
                {/* 0 leads is not 0% campaign coverage. */}
                <StatCard
                  label="With a campaign"
                  value={
                    current.totalLeads > 0
                      ? `${Math.round((current.withCampaign / current.totalLeads) * 100)}%`
                      : 'n/a'
                  }
                  hint={
                    current.totalLeads > 0
                      ? `${current.withCampaign} of ${current.totalLeads}`
                      : 'No leads captured this month'
                  }
                />
                <StatCard
                  label="CRM deals"
                  value={String(currentCrmEnquirySource.totalDeals)}
                  hint="Created this month"
                />
                {/* 0 deals is not 0% coverage: nothing was measured. */}
                <StatCard
                  label="CRM source coverage"
                  value={
                    currentCrmEnquirySource.coveragePct !== null
                      && currentCrmEnquirySource.totalDeals > 0
                      ? `${currentCrmEnquirySource.coveragePct}%`
                      : 'n/a'
                  }
                  hint={
                    currentCrmEnquirySource.totalDeals > 0
                      ? `${currentCrmEnquirySource.unknownDeals} unknown`
                      : 'No CRM deals created this month'
                  }
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <SourceMix month={current} prev={previous} />
                <SpendPanel
                  spendByProvider={current.spendByProvider}
                  spendNote={report.provenance.spendNote}
                />
              </div>

              <CampaignPanel
                byCampaign={current.byCampaign}
                spendByCampaign={current.spendByCampaign}
              />

              <EnquirySourcePanel
                data={currentCrmEnquirySource}
                note={report.provenance.enquirySourceNote}
              />

              <CommentaryPanels
                companyId={companyId || ''}
                month={current.month}
                onDirtyChange={setCommentaryDirty}
              />
            </>
          )}

          <p className="text-xs text-gray-500">
            Months are {report.provenance.timezone} calendar months. {report.provenance.spendNote}
          </p>
        </>
      )}
    </div>
  );
};

export default MarketingSourceReportPage;
